import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc.js'
import { z } from 'zod'
import { billCollectionStatusSchema, billStatusSchema, feeTypeSchema } from '@super-mega/server/contracts/bill'
import { createdAtSearchShape } from '@/utils/list-query.ts'

const optionalId = z.coerce.number().int().positive().optional().catch(undefined)
dayjs.extend(utc)
export const billSearchSchema = z.object({
  current: z.coerce.number().int().positive().catch(1),
  pageSize: z.coerce.number().int().min(1).max(100).catch(20),
  id: optionalId,
  keyword: z.string().trim().max(255).optional().catch(undefined),
  note: z.string().trim().max(255).optional().catch(undefined),
  orderType: z.enum(['rental', 'service']).optional().catch(undefined),
  orderGroupId: optionalId,
  rentalOrderId: optionalId,
  serviceOrderId: optionalId,
  creatorMemberId: optionalId,
  customerId: z
    .union([z.array(z.coerce.number().int().positive()).max(100), z.coerce.number().int().positive()])
    .optional()
    .catch(undefined),
  salespersonId: z
    .union([z.array(z.coerce.number().int().nonnegative()).max(100), z.coerce.number().int().nonnegative()])
    .optional()
    .catch(undefined),
  businessId: z
    .union([z.array(z.coerce.number().int().nonnegative()).max(100), z.coerce.number().int().nonnegative()])
    .optional()
    .catch(undefined),
  siteId: z.coerce.number().int().nonnegative().optional().catch(undefined),
  periodFrom: z.iso.date().optional().catch(undefined),
  periodTo: z.iso.date().optional().catch(undefined),
  organizationId: z
    .union([z.array(z.coerce.number().int().positive()).max(100), z.coerce.number().int().positive()])
    .optional()
    .catch(undefined),
  archived: z.enum(['active', 'archived', 'all']).catch('active'),
  status: z
    .union([z.array(billStatusSchema).max(100), billStatusSchema])
    .optional()
    .catch(undefined),
  collectionStatus: z
    .union([z.array(billCollectionStatusSchema).max(100), billCollectionStatusSchema])
    .optional()
    .catch(undefined),
  feeType: feeTypeSchema.optional().catch(undefined),
  settlement: z
    .enum(['outstanding', 'refundable', 'settled', 'unknown', 'notIssued', 'voided'])
    .optional()
    .catch(undefined),
  grouped: z.enum(['yes', 'no']).optional().catch(undefined),
  direction: z.literal('receivable').optional().catch(undefined),
  ...createdAtSearchShape,
  receivedFrom: z.iso.datetime({ offset: true }).optional().catch(undefined),
  receivedTo: z.iso.datetime({ offset: true }).optional().catch(undefined),
  sortField: z.enum(['createdAt', 'dueAt']).catch('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).catch('desc')
})
export type BillSearch = z.infer<typeof billSearchSchema>
export { DEFAULT_BILL_SEARCH } from './const/bill-search.ts'

export function billDate(value: Date | null) {
  return value ? dayjs.utc(value).format('YYYY-MM-DD') : '—'
}

export { BILL_COLLECTION_STATUS, BILL_KINDS, BILL_STATUS } from '@/features/bill/const/bill-search.ts'
